import { useState, useEffect, useRef, useMemo } from 'react';
import styled, { css } from 'styled-components';

import { Box } from '@hivebrite/design-system/Box';
import { Container } from '@hivebrite/design-system/Container';
import { FakeLink } from '@hivebrite/design-system/Link';
import type { SimpleBarJS } from '@hivebrite/design-system/Simplebar';
import { Simplebar } from '@hivebrite/design-system/Simplebar';
import { TagsList } from '@hivebrite/design-system/TagsList';

import type { TagsFiltersProps } from './types';

// Pretty arbitrary number...
const GRADIENT_WIDTH = 30;

const TagsFilters = ({
  labelledBy,
  multi,
  tags,
  selectedTags = [],
  showAllTag,
  limit,
  onClick,
  singleRow,
}: TagsFiltersProps) => {
  const scrollableNodeRef = useRef<SimpleBarJS | null>(null);

  const [tagsAreLimited, setTagsAreLimited] = useState(false);
  const [showLeftGradient, setShowLeftGradient] = useState(false);
  const [showRightGradient, setShowRightGradient] = useState(false);

  // When the filters go to the query string in the url and back, ints are returned
  // as strings. So even if we provided an int for the name we want to convert it to
  // a string here in order for the selected tags to be properly reloaded when sharing
  // urls or reloading the page.
  const stringNameTags = useMemo(
    () => tags?.map(({ name, ...rest }) => ({ name: `${name}`, ...rest })),
    [tags],
  );

  useEffect(() => {
    if (stringNameTags?.length > 0) {
      setTagsAreLimited(!!limit && stringNameTags.length > limit);
    }
  }, [stringNameTags, limit]);

  const updateGradientVisibility = () => {
    const { scrollLeft, scrollWidth, offsetWidth } =
      scrollableNodeRef.current?.contentWrapperEl ?? {};

    setShowLeftGradient(scrollLeft > 0);
    setShowRightGradient(scrollLeft + offsetWidth < scrollWidth);
  };

  useEffect(() => {
    if (scrollableNodeRef.current) {
      // Add event listeners
      scrollableNodeRef.current.contentWrapperEl.addEventListener(
        'scroll',
        updateGradientVisibility,
      );
      window.addEventListener('resize', updateGradientVisibility);

      // Initialize states
      updateGradientVisibility();

      const simpleBarWrapperEl = scrollableNodeRef.current.contentWrapperEl;

      return () => {
        simpleBarWrapperEl.removeEventListener(
          'scroll',
          updateGradientVisibility,
        );
        window.removeEventListener('resize', updateGradientVisibility);
      };
    }
  }, []);

  const Wrapper = singleRow ? SimpleBarWrapper : NoWrapper;

  if (!stringNameTags?.length) {
    return null;
  }

  return (
    <Container
      height="auto"
      css={css`
        justify-content: flex-start;
        position: relative;
      `}
    >
      <Wrapper
        simpleBarRef={scrollableNodeRef}
        showLeftGradient={showLeftGradient}
        showRightGradient={showRightGradient}
      >
        <Box
          css={css`
            flex-wrap: ${singleRow ? 'nowrap' : 'wrap'};
          `}
          mb={-3}
          alignItems="center"
          data-testid="tags"
          role="group"
          aria-labelledby={labelledBy}
        >
          <TagsList
            multi={multi}
            showAllTag={showAllTag}
            tags={
              tagsAreLimited ? stringNameTags.slice(0, limit) : stringNameTags
            }
            selectedTagNames={selectedTags}
            onChange={onClick}
          />
          {!!limit && stringNameTags.length > limit && (
            <FakeLink
              mb={3}
              mr={3}
              onClick={() => setTagsAreLimited((state) => !state)}
              flex="0 0 auto"
            >
              {tagsAreLimited ? s__('Tags|See all') : s__('Tags|See less')}
            </FakeLink>
          )}
        </Box>
      </Wrapper>
    </Container>
  );
};

export default TagsFilters;

const SimpleBarWrapper = ({
  children,
  simpleBarRef,
  showLeftGradient,
  showRightGradient,
}) => (
  <>
    <Simplebar
      style={{
        width: '100%',
        height: '44px',
      }}
      ref={simpleBarRef}
    >
      {children}
    </Simplebar>
    {showLeftGradient && <GradientLeft $width={GRADIENT_WIDTH} />}
    {showRightGradient && <GradientRight $width={GRADIENT_WIDTH} />}
  </>
);

const NoWrapper = ({ children }) => children;

const bgColor = `245, 246, 246`;
const bgColorMaskGradient = `var(--color-background, rgb(${bgColor})), var(--color-background-transparent, rgba(${bgColor}, 0))`;

const GradientLeft = styled.div<{ $width: number }>`
  position: absolute;
  left: 0px;
  height: 100%;
  background: linear-gradient(to right, ${bgColorMaskGradient});
  width: ${({ $width }) => $width}px;
`;

const GradientRight = styled.div<{ $width: number }>`
  position: absolute;
  right: 0px;
  height: 100%;
  background: linear-gradient(to left, ${bgColorMaskGradient});
  width: ${({ $width }) => $width}px;
`;
